import React, { useCallback, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import BlogCard from "./BlogCard";
import { BlogPost } from "../data/blogPosts";

const CARD_WIDTH = 340;
const GAP = 24;
const STEP = CARD_WIDTH + GAP;
const SPEED_DESKTOP = 30;  // px/s → ~12s por card
const SPEED_MOBILE = 45;   // px/s → ~8s por card

const BlogCarousel: React.FC<{ posts: BlogPost[] }> = ({ posts }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const posRef = useRef(0);
  const pausedRef = useRef(false);
  const lastTsRef = useRef<number | null>(null);
  const rafRef = useRef<number>(0);
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (posts.length === 0) return null;

  const totalWidth = posts.length * STEP;
  const items = [...posts, ...posts];

  const applyTransform = useCallback(() => {
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(-${posRef.current}px)`;
    }
  }, []);

  const tick = useCallback((ts: number) => {
    if (!pausedRef.current) {
      if (lastTsRef.current !== null) {
        const speed = window.innerWidth < 768 ? SPEED_MOBILE : SPEED_DESKTOP;
        posRef.current += (speed * (ts - lastTsRef.current)) / 1000;
        if (posRef.current >= totalWidth) posRef.current -= totalWidth;
        applyTransform();
      }
      lastTsRef.current = ts;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [totalWidth, applyTransform]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [tick]);

  const navigate = useCallback((direction: 1 | -1) => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    pausedRef.current = true;
    lastTsRef.current = null;

    posRef.current += direction * STEP;
    if (posRef.current < 0) posRef.current += totalWidth;
    if (posRef.current >= totalWidth) posRef.current -= totalWidth;
    applyTransform();

    resumeTimerRef.current = setTimeout(() => {
      pausedRef.current = false;
    }, 2000);
  }, [totalWidth, applyTransform]);

  return (
    <div className="relative w-full group">
      <div
        className="overflow-hidden w-full"
        onMouseEnter={() => { pausedRef.current = true; lastTsRef.current = null; }}
        onMouseLeave={() => { pausedRef.current = false; }}
      >
        <div
          ref={trackRef}
          className="flex"
          style={{ willChange: "transform" }}
        >
          {items.map((post, index) => (
            <div
              key={`${post.id}-${index}`}
              style={{ width: `${CARD_WIDTH}px`, marginRight: `${GAP}px`, flexShrink: 0 }}
            >
              <BlogCard post={post} index={index % posts.length} href={`/blog/${post.slug}`} />
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={() => navigate(-1)}
        aria-label="Post anterior"
        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#2a3c5c] rounded-full p-2 shadow-md max-md:opacity-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
      >
        <ChevronLeft size={22} />
      </button>

      <button
        onClick={() => navigate(1)}
        aria-label="Próximo post"
        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#2a3c5c] rounded-full p-2 shadow-md max-md:opacity-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
      >
        <ChevronRight size={22} />
      </button>
    </div>
  );
};

export default BlogCarousel;
